import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { GetMappingData } from 'models/requestBody/updatePropertyValues.interface';
import { STATUS } from 'projectConstants';
import apiService from 'services';

/**
 * Build a short, user-visible message from axios/API errors (e.g. 4xx/5xx on action save).
 */
function getApiErrorMessage(error: unknown, fallback: string): string {
  const axiosLikeError = error as { response?: { data?: unknown }; message?: string };
  const data = axiosLikeError?.response?.data;
  if (typeof data === 'string' && data.trim()) {
    const text = data.replace(/<[^>]+>/g, ' ').trim();
    return text.length > 400 ? `${text.slice(0, 400)}…` : text;
  }
  if (data && typeof data === 'object' && data !== null) {
    const responseBody = data as { message?: string; error?: string };
    if (typeof responseBody.message === 'string' && responseBody.message.trim()) {
      return responseBody.message;
    }
    if (typeof responseBody.error === 'string' && responseBody.error.trim()) {
      return responseBody.error;
    }
  }
  if (typeof axiosLikeError?.message === 'string' && axiosLikeError.message.trim()) {
    return axiosLikeError.message;
  }
  return fallback;
}

export const uploadDataImportFile = createAsyncThunk(
  'uploadFile/post',
  async ({ modelId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.uploadExcelFileData({ modelId, request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);
export const getMetaDataFile = createAsyncThunk(
  'getMetaDataFile/get',
  async (modelId: any, thunkAPI) => {
    try {
      const response = await apiService.getMetadata(modelId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const getDataImportFiles = createAsyncThunk(
  'getDataImportFiles/get',
  async (modelId: any, thunkAPI) => {
    try {
      const response = await apiService.getExcelFiles(modelId);
      return response?.data?.data_source;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const getSingleDataImportFiles = createAsyncThunk(
  'getSingleDataImportFiles/fetch',
  async ({ modelId, fileId }: any, thunkAPI) => {
    try {
      const response = await apiService.getSingleExcelFile(modelId, fileId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const deleteDataImportFile = createAsyncThunk(
  'deleteDataImportFile/delete',
  async ({ modelId, fileId }: any, thunkAPI) => {
    try {
      const response = await apiService.deleteSingleExcelFile(modelId, fileId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const editDataImportFile = createAsyncThunk(
  'deleteDataImportFile/delete',
  async ({ modelId, fileId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.editExcelFile({ modelId, fileId, request_body });
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const getActionData = createAsyncThunk(
  'getActionData/fetch',
  async (dataSourceId: number, thunkAPI) => {
    try {
      const response = await apiService.allActionData(dataSourceId);
      return response?.data?.action;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteActionData = createAsyncThunk(
  'getActionData/delete',
  async ({ dataSourceId, actionId }: any, thunkAPI) => {
    try {
      const response = await apiService.deleteDataAction(dataSourceId, actionId);
      return response?.data?.action;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const getOneActionData = createAsyncThunk(
  'getOneActionData/fetch',
  async ({ dataSourceId, actionId }: any, thunkAPI) => {
    try {
      const response = await apiService.getDataAction(dataSourceId, actionId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const createAction = createAsyncThunk(
  'createAction/post',
  async ({ dataSourceId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.createActionData({ dataSourceId, request_body });
      return response?.data;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(
        getApiErrorMessage(error, 'Unable to create the action. Please try again.'),
      );
    }
  },
);
export const putActionData = createAsyncThunk(
  'putActionData/put',
  async ({ dataSourceId, actionId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.editActionData({ dataSourceId, actionId, request_body });
      return response.data;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(
        getApiErrorMessage(error, 'Unable to update the action. Please try again.'),
      );
    }
  },
);

export const editActionDetails = createAsyncThunk(
  'editActionDetails/post',
  async ({ dataSourceId, actionId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.editActionData({ dataSourceId, actionId, request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const versionData = createAsyncThunk(
  'versionData/get',
  async (dataSourceId: number, thunkAPI) => {
    try {
      const response = await apiService.getVersionData(dataSourceId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const getVersionCompare = createAsyncThunk(
  'getVersionCompare/post',
  async ({ targetId, sourceId }: any, thunkAPI) => {
    try {
      const response = await apiService.getVersionCompare(sourceId, targetId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const postVersionFile = createAsyncThunk(
  'postVersionFile/post',
  async ({ dataSourceId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.postVersionFile({ dataSourceId, request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const editVersionFile = createAsyncThunk(
  'postVersionFile/post',
  async ({ dataSourceId, versionId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.editVersionFile({ dataSourceId, versionId, request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const deleteVersionIFile = createAsyncThunk(
  'deleteVersionIFile/delete',
  async ({ dataSourceId, versionId }: any, thunkAPI) => {
    try {
      const response = await apiService.deleteVersionFile(dataSourceId, versionId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const getMappingDataFile = createAsyncThunk(
  'getMappingData/post',
  async ({ dataSourceId, actionId, parameters }: GetMappingData, thunkAPI) => {
    try {
      const response = await apiService.getDataMapping(dataSourceId, actionId, {
        params: parameters,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);
export const postMappingData = createAsyncThunk(
  'postMappingData/post',
  async ({ dataSourceId, actionId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.runDataMapping({ dataSourceId, actionId, request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const duplicateSourceFile = createAsyncThunk(
  'duplicateSourceFile/post',
  async (dataSourceId: number, thunkAPI) => {
    try {
      const response = await apiService.duplicateSourceData(dataSourceId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const duplicateActionFile = createAsyncThunk(
  'duplicateActionFile/post',
  async ({ dataSourceId, actionId }: any, thunkAPI) => {
    try {
      const response = await apiService.duplicateActionData(dataSourceId, actionId);
      return response?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchDimensionItems = createAsyncThunk(
  'fetchDimensionItems/fetch',
  async ({ dimensionId, parameters }: any, thunkAPI) => {
    try {
      const response = await apiService.get_dimension_items(dimensionId, {
        params: parameters,
      });
      return response.data.items;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
const initialState: any = {
  importDataFiles: null,
  metadata: null,
  actionSteps: 0,
  isActionPopoverOpen: false,
  actionFormData: {
    name: '',
    description: '',
    dataType: '',
    dimension: '',
    blockId: null,
    selectedScenario: null,
    column: '',
    propertdata: null,
    dataPeriod: '1',
    startDate: '',
    selectedTimePeriod: null,
    singleTimePeriodValue: null,
    multiTimePeriodValue: null,
    timeValueMap: null,
    timePeriodRowIndex: null,
    importIndicatorType: '1',
    singleIndicatorValue: null,
    multipleIndicatprType: null,
    rowsIndicatorMappedData: null,
    columnIndicatorMappedData: null,
    columnNumericValueoofRowsIndicatorType: null,
    selectedDimensionColumnofTable: null,
    dimensionItemsData: null,
    // Unified sync_options — sent directly to API as a JSON object.
    // Structure varies by action type (see plan docs for schema).
    syncOptions: null,
  },
  selectedDataFileDetails: {
    id: null,
    name: null,
    deatsilsPage: false,
  },
  tableHeaderList: [],
  versionList: [],
  selectedVersionDetails: {},
  actionListData: null,
  mappedDataFile: {},
  mappedDataFileStatus: STATUS.IDLE,
  postMappingDataStatus: STATUS.IDLE,
  selectedares: {
    selectedColumnOption: '',
  },
  isEdit: {
    isEditForm: false,
    actionId: null,
    actionType: null,
    scenarioId: null,
  },
  selectedAction: {
    actionId: null,
  },
  actionCreateForm: false,
  noHeaders: false,
  distinctCSV: null,
  pendingDataActionPayload: null,
  steps: 3,
  importRunResult: null,
  // Removal / sync options for dimension import (ActionStepFour)
  removeMissingItems: false,
  removalScopeMode: 'all',
  removalFilterExpression: null,
  /** When true, load data previews via modelAPI instead of full S3 download. */
  dataSourceApiPreviewEnabled: true,
};

const dataImportSlice: any = createSlice({
  initialState,
  name: 'dataImportSlice',
  reducers: {
    resetDataAll: (state) => {
      state.importDataFiles = null;
      state.distinctCSV = null;
      state.steps = 3;
      state.selectedDataFileDetails = {
        id: null,
        name: null,
        deatsilsPage: false,
      };
      state.versionList = [];
      state.selectedVersionDetails = {};
      state.selectedares = {
        selectedColumnOption: '',
      };
      state.isEdit = {
        isEditForm: false,
        actionId: null,
        actionType: null,
        scenarioId: null,
      };
      state.selectedAction = {
        actionId: null,
      };
      state.metadata = null;
      state.actionFormData = initialState.actionFormData;
      state.actionSteps = 0;
      state.noHeaders = false;
      state.tableHeaderList = [];
      state.actionListData = null;
      state.mappedDataFile = {};
      state.mappedDataFileStatus = STATUS.IDLE;
      state.postMappingDataStatus = STATUS.IDLE;
      state.actionCreateForm = false;
      state.noHeaders = false;
      state.pendingDataActionPayload = null;
      state.removeMissingItems = false;
      state.removalScopeMode = 'all';
      state.removalFilterExpression = null;
    },
    stepsLength: (state, action) => {
      state.steps = action.payload;
    },
    setDistinctCSV: (state, action) => {
      state.distinctCSV = action.payload;
    },
    setDataSourceApiPreviewEnabled: (state, action) => {
      state.dataSourceApiPreviewEnabled = Boolean(action.payload);
    },
    setNoheaders: (state, action) => {
      state.noHeaders = action.payload;
    },
    setPendingDataActionPayload: (state, action) => {
      state.pendingDataActionPayload = action.payload;
    },
    setDataPeriodType: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        dataPeriod: action.payload,
      };
    },
    resetDataPeriodValuesForSingleType: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        column: '',
        dimension: '',
        propertdata: null,
        selectedTimePeriod: null,
        // singleTimePeriodValue: null,
        multiTimePeriodValue: null,
        // importIndicatorType: '1',
        // singleIndicatorValue: null,
        // multipleIndicatprType: null,
        // rowsIndicatorMappedData: null,
        // columnIndicatorMappedData: null,
        // columnNumericValueoofRowsIndicatorType: null,
        // selectedDimensionColumnofTable: null,
        // dimensionItemsData: null,
      };
    },
    resetDataPeriodValuesForMultipleType: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        column: '',
        dimension: '',
        propertdata: null,
        startDate: '',
        // singleTimePeriodValue: null,
        // multiTimePeriodValue: null,
        // importIndicatorType: '1',
        // singleIndicatorValue: null,
        // multipleIndicatprType: null,
        // rowsIndicatorMappedData: null,
        // columnIndicatorMappedData: null,
        // columnNumericValueoofRowsIndicatorType: null,
        // selectedDimensionColumnofTable: null,
        // dimensionItemsData: null,
      };
    },
    setImportIndicatorType: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        importIndicatorType: action.payload,
      };
    },
    resetDataImport: (state) => {
      state.selectedDataFileDetails = {
        id: null,
        name: null,
        deatsilsPage: false,
      };
      state.steps = 3;
      state.actionListData = null;
      state.versionList = [];
      state.selectedVersionDetails = {};
      state.selectedares = {
        selectedColumnOption: '',
      };
      state.isEdit = {
        isEditForm: false,
        actionId: null,
        actionType: null,
        scenarioId: null,
      };
      state.selectedAction = {
        actionId: null,
      };
      state.metadata = null;
    },
    selectedFileId: (state, action) => {
      state.selectedDataFileDetails = action.payload;
    },
    handleActionStepNext: (state) => {
      state.actionSteps += 1;
    },
    handleActionStepPrevious: (state) => {
      state.actionSteps -= 1;
    },
    handleActionPopover: (state, action) => {
      state.isActionPopoverOpen = action.payload;
    },
    addSteponeFormData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        name: action.payload?.name,
        description: action.payload?.description,
        duplicateAction: action.payload?.duplicateAction,
      };
    },
    addModalFormData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        dataType: action.payload,
      };
    },
    addStepTwoFormData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        dataType: action.payload?.selectedDataType,
        dimension: action.payload?.selecetedDimension,
        column: action.payload?.selectedColumnOption,
        blockId: action.payload?.selectedBlock,
        selectedScenario: action.payload?.selectedScenario,
      };
    },
    addDataStepOneFormSignleDataPeriod: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        startDate: action.payload?.selectedStartDate
          ? new Date(action.payload?.selectedStartDate).toDateString()
          : '',
      };
    },
    addDataStepOneFormMultipleDataPeriod: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        selectedTimePeriod: action.payload?.selectedTimePeriod,
      };
    },
    setSingleTimePeriodValue: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        singleTimePeriodValue: action.payload?.singleTimePeriodValue,
      };
    },
    setTimePeriodRowIndex: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        timePeriodRowIndex: action.payload,
      };
    },
    resetSingleTimePeriodValue: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        singleTimePeriodValue: null,
        timeValueMap: null,
      };
    },
    resetMultipleTimePeriodValue: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        multiTimePeriodValue: null,
        timePeriodRowIndex: null,
      };
    },
    setMultiTimePeriodValue: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        multiTimePeriodValue: action.payload,
      };
    },
    setTimeValueMap: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        timeValueMap: action.payload,
      };
    },
    setSingleIndicatorValue: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        singleIndicatorValue: action.payload?.singleIndicatorValue,
        columnNumericValueoofRowsIndicatorType:
          action.payload?.columnNumericValueoofRowsIndicatorType,
      };
    },
    resetSingleIndicatorValue: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        singleIndicatorValue: null,
      };
    },

    setMultipleIndicatorsType: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        multipleIndicatorsType: action.payload?.multipleIndicatorsType,
      };
    },
    resetMultipleIndicatorsType: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        multipleIndicatorsType: null,
      };
    },
    setRowsIndicatorMappedData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        rowsIndicatorTableColumn: action.payload?.rowsIndicatorTableColumn,
        rowsIndicatorMappedData: action.payload?.data,
      };
    },
    setColumnNumericValueoofRowsIndicatorType: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        columnNumericValueoofRowsIndicatorType:
          action.payload?.columnNumericValueoofRowsIndicatorType,
      };
    },
    resetRowsIndicatorMappedData: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        rowsIndicatorTableColumn: null,
        rowsIndicatorMappedData: null,
        columnNumericValueoofRowsIndicatorType: null,
      };
    },
    setColumnIndicatorMappedData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        columnIndicatorMappedData: action.payload,
      };
    },
    resetColumnIndicatorMappedData: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        columnIndicatorMappedData: null,
      };
    },
    setSelectedDimensionColumnofTable: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        selectedDimensionColumnofTable: action.payload?.selectedDimensionColumnofTable,
      };
    },
    /**
     * Persist the property mapping Formik form values into Redux so downstream
     * wizard steps (e.g. the sync options step) can read them when assembling
     * the final API request body.
     */
    setPropertyMappingData: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        propertdata: action.payload,
      };
    },
    /**
     * Set the entire sync_options object (sent verbatim to the API).
     * Both dimension and data/forecast action types use this field.
     */
    setSyncOptions: (state, action) => {
      state.actionFormData = {
        ...state.actionFormData,
        syncOptions: action.payload,
      };
    },
    clearStepperFormData: (state) => {
      state.actionFormData = initialState.actionFormData;
      state.isEdit = initialState.isEdit;
      state.actionSteps = 0;
      state.noHeaders = false;
      state.pendingDataActionPayload = null;
      state.removeMissingItems = false;
      state.removalScopeMode = 'all';
      state.removalFilterExpression = null;
    },
    clearStepperFormPropertyData: (state) => {
      state.actionFormData = {
        ...state.actionFormData,
        propertdata: {},
      };
    },
    setTableHeaderName: (state, action) => {
      state.tableHeaderList = action.payload;
    },
    setSelectedVersionDetails: (state, action) => {
      state.selectedVersionDetails = action.payload;
    },
    setSelectedAreas: (state, action) => {
      state.selectedares = {
        ...state.selectedares,
        selectedColumnOption: action.payload?.selectedColumnOption,
      };
    },
    setSelectedAreasProperties: (state, action) => {
      state.selectedares = {
        ...state.selectedares,
        ...action.payload,
      };
    },
    setClearSelectedAreasTable: (state) => {
      state.selectedares = initialState.selectedares;
    },
    setActionFormEdit: (state, action) => {
      state.isEdit = action.payload;
    },
    setActionDetails: (state, action) => {
      state.selectedAction = action.payload;
    },
    resetMappingData: (state) => {
      state.mappedDataFile = initialState.mappedDataFile;
      state.mappedDataFileStatus = initialState.mappedDataFileStatus;
    },
    setActionCreateForm: (state, action) => {
      state.actionCreateForm = action.payload;
    },
    /**
     * Toggle whether items missing from the imported file should be removed
     * from the dimension after the import completes.
     */
    setRemoveMissingItems: (state, action) => {
      state.removeMissingItems = action.payload;
    },
    /**
     * Set the removal scope: 'all' removes any missing item in the dimension;
     * 'filter' restricts removal to items that match the filter expression.
     */
    setRemovalScopeMode: (state, action) => {
      state.removalScopeMode = action.payload;
    },
    /**
     * Persist the filter expression used for scoped removal so it survives
     * wizard navigation and can be re-hydrated when editing an action.
     */
    setRemovalFilterExpression: (state, action) => {
      state.removalFilterExpression = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDataImportFiles.fulfilled, (state, action) => {
      if (action.payload?.length === 1) {
        state.selectedDataFileDetails = {
          id: action.payload[0].id,
          name: action.payload[0].file_name,
          deatsilsPage: true,
        };
      }
      state.importDataFiles = action.payload;
    });
    builder.addCase(versionData.fulfilled, (state, action) => {
      state.versionList = action.payload;
      state.selectedVersionDetails = action.payload?.versions[0];
    });
    builder.addCase(getMetaDataFile.fulfilled, (state, action) => {
      state.metadata = action.payload;
    });
    builder.addCase(getActionData.fulfilled, (state, action) => {
      state.actionListData = action.payload;
    });
    builder.addCase(getMappingDataFile.fulfilled, (state, action) => {
      state.mappedDataFile = action.payload;
    });
    builder.addCase(getMappingDataFile.rejected, (state) => {
      state.mappedDataFileStatus = STATUS.FAILED;
    });
    builder.addCase(postMappingData.fulfilled, (state, action) => {
      state.postMappingDataStatus = STATUS.SUCCESS;
      // Store the run result for display in MappingSaved
      if (action.payload && action.payload.status) {
        state.importRunResult = action.payload;
      }
    });
    builder.addCase(postMappingData.rejected, (state) => {
      state.postMappingDataStatus = STATUS.FAILED;
      state.importRunResult = null;
    });
    builder.addCase(getOneActionData.fulfilled, (state, action) => {
      const mappingData = action.payload?.mappings[0];
      const dimId = Object.keys(mappingData)[0];
      let reversedColumnMappingData: any = {};
      if (action.payload?.indicator_mapping_type === 'column') {
        reversedColumnMappingData = Object.keys(mappingData?.indicator_map).map((key) => {
          return {
            key: mappingData?.indicator_map[key],
            value: Number.parseInt(key, 10),
          };
        });
      }
      reversedColumnMappingData =
        Object.keys(reversedColumnMappingData).length > 0 ? reversedColumnMappingData : null;
      let reversedRowMappingData: any = {};
      if (action.payload?.indicator_mapping_type === 'row' && mappingData?.indicator_map) {
        Object.keys(mappingData?.indicator_map).forEach((key) => {
          reversedRowMappingData[mappingData?.indicator_map[key]] = key;
        });
      }
      reversedRowMappingData =
        Object.keys(reversedRowMappingData).length > 0 ? reversedRowMappingData : null;
      let editDataTypeRadio: '1' | '2' | '3' = '2';
      if (action.payload?.action_type === 'dimension') {
        editDataTypeRadio = '1';
      } else if (action.payload?.action_type === 'forecast') {
        editDataTypeRadio = '3';
      }

      state.actionFormData = {
        name: action.payload?.name,
        description: action.payload?.description,
        dataType: editDataTypeRadio,
        dimension: dimId,
        column: mappingData[dimId],
        blockId: mappingData?.block_id,
        selectedTimePeriod: action.payload?.mapping_type,
        propertdata: mappingData?.property_map,
        startDate: action.payload?.period_start_date,
        dataPeriod: action.payload?.period_type === 'single' ? '1' : '2',
        selectedScenario: action.payload?.scenario_id,
        singleTimePeriodValue: action.payload?.time_column,
        timePeriodRowIndex: mappingData?.time_period_row_index ?? null,
        importIndicatorType: action.payload?.is_multiple_indicator ? '2' : '1',
        multipleIndicatorsType: action.payload?.indicator_mapping_type,
        rowsIndicatorTableColumn: mappingData?.indicator_column,
        columnNumericValueoofRowsIndicatorType: mappingData?.value_column,
        columnIndicatorMappedData: reversedColumnMappingData,
        rowsIndicatorMappedData: reversedRowMappingData,
        singleIndicatorValue: mappingData?.indicator,
        multiTimePeriodValue: mappingData?.time_column_map,
        timeValueMap: mappingData?.time_value_map,
        dimensionItemsData: mappingData?.block_dimension_map,
        mapItemsManually: action.payload?.mapItemsManually,
        syncOptions: action.payload?.sync_options ?? null,
      };

      state.noHeaders = action.payload?.missing_header;
      state.selectedares =
        action.payload?.action_type === 'dimension'
          ? {
              selectedColumnOption: mappingData[dimId],
              ...mappingData?.property_map,
            }
          : mappingData?.shaddedArea;
    });
  },
});

export const {
  selectedFileId,
  handleActionPopover,
  handleActionStepNext,
  handleActionStepPrevious,
  addSteponeFormData,
  addStepTwoFormData,
  setTableHeaderName,
  addModalFormData,
  resetDataImport,
  setSelectedVersionDetails,
  clearStepperFormData,
  setSelectedAreas,
  setSelectedAreasProperties,
  setClearSelectedAreasTable,
  clearStepperFormPropertyData,
  setActionFormEdit,
  setActionDetails,
  resetMappingData,
  setActionCreateForm,
  addDataStepOneFormSignleDataPeriod,
  setNoheaders,
  setPendingDataActionPayload,
  resetDataAll,
  setDataPeriodType,
  addDataStepOneFormMultipleDataPeriod,
  setSingleTimePeriodValue,
  setTimePeriodRowIndex,
  setMultiTimePeriodValue,
  setTimeValueMap,
  setImportIndicatorType,
  setSingleIndicatorValue,
  setMultipleIndicatorsType,
  setRowsIndicatorMappedData,
  setColumnIndicatorMappedData,
  setColumnNumericValueoofRowsIndicatorType,
  setSelectedDimensionColumnofTable,
  setDistinctCSV,
  setDataSourceApiPreviewEnabled,
  resetDataPeriodValuesForSingleType,
  resetDataPeriodValuesForMultipleType,
  resetSingleTimePeriodValue,
  resetMultipleTimePeriodValue,
  resetMultipleIndicatorsType,
  resetSingleIndicatorValue,
  resetColumnIndicatorMappedData,
  resetRowsIndicatorMappedData,
  stepsLength,
  setPropertyMappingData,
  setSyncOptions,
  setRemoveMissingItems,
  setRemovalScopeMode,
  setRemovalFilterExpression,
} = dataImportSlice.actions;
export const getImportDataFiles = (state: any) => state.dataImportSlice.importDataFiles;
export const getSelectedDataFileDetails = (state: any) =>
  state.dataImportSlice.selectedDataFileDetails;

export const getFormValues = (state: any) => state.dataImportSlice.actionFormData;
export const getTableHeader = (state: any) => state.dataImportSlice.tableHeaderList;
export const getVersionList = (state: any) => state.dataImportSlice.versionList;
export const getSelectedVersionDetails = (state: any) =>
  state.dataImportSlice.selectedVersionDetails;
export const getActionList = (state: any) => state.dataImportSlice.actionListData;
export const getMappedDataFile = (state: any) => state.dataImportSlice.mappedDataFile;
export const getPostMappingDataStatus = (state: any) => state.dataImportSlice.postMappingDataStatus;
export const getSelectedAreaOfTable = (state: any) => state.dataImportSlice.selectedares;
export const getEditActionForm = (state: any) => state.dataImportSlice.isEdit;
export const getSelectedAction = (state: any) => state.dataImportSlice.selectedAction;
export const getMappedDataFileStatus = (state: any) => state.dataImportSlice.mappedDataFileStatus;
export const getActionCreateStatus = (state: any) => state.dataImportSlice.actionCreateForm;
export const getMetadatas = (state: any) => state.dataImportSlice.metadata;
export const getNoheadersStatus = (state: any) => state.dataImportSlice.noHeaders;
export const getDataSourceApiPreviewEnabled = (state: any) =>
  state.dataImportSlice.dataSourceApiPreviewEnabled;
export const getPendingDataActionPayload = (state: any) =>
  state.dataImportSlice.pendingDataActionPayload;
export const getDistinctCSV = (state: any) => state.dataImportSlice.distinctCSV;
export const getStepsLength = (state: any) => state.dataImportSlice.steps;
export const getSyncOptions = (state: any) => state.dataImportSlice.actionFormData.syncOptions;
export const getImportRunResult = (state: any) => state.dataImportSlice.importRunResult;

/** Whether the "remove missing items" toggle is enabled. */
export const getRemoveMissingItems = (state: any): boolean =>
  state.dataImportSlice.removeMissingItems;

/** The removal scope: 'all' | 'filter'. */
export const getRemovalScopeMode = (state: any): string => state.dataImportSlice.removalScopeMode;

/**
 * The persisted filter expression used when removalScopeMode is 'filter'.
 * Shape: `{ logic: 'AND'; conditions: FilterCondition[] } | null`.
 */
export const getRemovalFilterExpression = (state: any) =>
  state.dataImportSlice.removalFilterExpression as {
    logic: 'AND';
    conditions: { property_id: string; operator: string; value: string }[];
  } | null;

export default dataImportSlice.reducer;
